import React, { useState, useEffect } from 'react';
import { Settings, ExternalLink, Check, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { useJiraSync } from '../hooks';

export function SettingsView() {
  const { loading, error, saveCredentials, testConnection, getConfig } = useJiraSync();

  const [jiraHost, setJiraHost] = useState('');
  const [jiraUsername, setJiraUsername] = useState('');
  const [jiraPassword, setJiraPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [config, setConfig] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Load existing config on mount
  useEffect(() => {
    async function loadConfig() {
      try {
        const result = await getConfig();
        if (result.configured) {
          setConfig(result.config);
          setJiraHost(result.config.jiraHost);
          setJiraUsername(result.config.jiraUsername);
        }
      } catch (err) {
        console.error('Error loading config:', err);
      } finally {
        setLoadingConfig(false);
      }
    }
    loadConfig();
  }, [getConfig]);

  const handleSave = async () => {
    setSaveSuccess(false);
    setTestResult(null);
    try {
      await saveCredentials({ jiraHost, jiraUsername, jiraPassword });
      setSaveSuccess(true);
      setJiraPassword(''); // Clear password after save
      // Reload config
      const result = await getConfig();
      if (result.configured) {
        setConfig(result.config);
      }
    } catch (err) {
      console.error('Error saving credentials:', err);
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      const result = await testConnection();
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Settings className="text-gray-600" size={24} />
        <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
      </div>

      {/* Jira Integration Section */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Jira Integration</h3>
          {config && (
            <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">
              Configured
            </span>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Connect your Jira account to sync time entries as worklogs. You'll need your Jira host,
          email, and an API token.{' '}
          <a
            href="https://id.atlassian.com/manage-profile/security/api-tokens"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            Create API token <ExternalLink size={14} />
          </a>
        </p>

        {/* Current Config Info */}
        {config && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Host:</span>
                <span className="ml-2 text-gray-900">{config.jiraHost}</span>
              </div>
              <div>
                <span className="text-gray-500">Username:</span>
                <span className="ml-2 text-gray-900">{config.jiraUsername}</span>
              </div>
              <div>
                <span className="text-gray-500">Configured:</span>
                <span className="ml-2 text-gray-900">{formatDate(config.configuredAt)}</span>
              </div>
              <div>
                <span className="text-gray-500">Last tested:</span>
                <span className="ml-2 text-gray-900">{formatDate(config.lastTestedAt)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Configuration Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Jira Host
            </label>
            <input
              type="text"
              value={jiraHost}
              onChange={(e) => setJiraHost(e.target.value)}
              placeholder="company.atlassian.net"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email / Username
            </label>
            <input
              type="email"
              value={jiraUsername}
              onChange={(e) => setJiraUsername(e.target.value)}
              placeholder="you@company.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Token
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={jiraPassword}
                onChange={(e) => setJiraPassword(e.target.value)}
                placeholder={config ? '••••••••••••••••' : 'Enter your API token'}
                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {config && (
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to keep existing token
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Success Message */}
          {saveSuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
              <Check size={18} />
              <span className="text-sm">Credentials saved successfully</span>
            </div>
          )}

          {/* Test Result */}
          {testResult && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg ${
                testResult.success
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}
            >
              {testResult.success ? <Check size={18} /> : <AlertCircle size={18} />}
              <span className="text-sm">
                {testResult.success
                  ? `Connected as ${testResult.user?.displayName}`
                  : testResult.error}
              </span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={loading || !jiraHost || !jiraUsername || (!jiraPassword && !config)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : null}
              Save Credentials
            </button>

            {config && (
              <button
                onClick={handleTest}
                disabled={loading}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                Test Connection
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
        <h3 className="text-base font-semibold text-blue-900 mb-3">How it works</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>• Include a Jira issue number in your time entry description (e.g., "ABC-123 - Working on feature")</li>
          <li>• Go to Reports and click "Sync to Jira" to sync completed entries</li>
          <li>• Entries will be added as worklogs on the matching Jira issues</li>
          <li>• Your credentials are encrypted and stored securely</li>
        </ul>
      </div>
    </div>
  );
}
